<template>
  <div class="career flex flex-col mt-6 pb-16">
    <div class="career__intro">
      <h3>Carreira</h3>
      <p>
        Ferramentas da Recrutei para você se preparar{{ clubAvailable ? " e economizar" : "" }} enquanto busca a próxima
        oportunidade.
      </p>
    </div>

    <!-- Mesmas sub-abas da aba Vagas (.nxp-nav-button). -->
    <div class="career__nav">
      <button
        v-for="item in tabs"
        :key="item.key"
        type="button"
        :class="['career__nav-button', { 'is-active': tab === item.key }]"
        :style="tab === item.key ? { borderColor: color } : {}"
        @click="select(item.key)"
      >
        {{ item.label }}
      </button>
    </div>

    <div class="career__content">
      <public-profile-card v-if="tab === 'perfil'" source="career" />

      <recrutest-banner v-else-if="tab === 'teste'" />

      <!-- O treino é por candidatura: o link leva a vaga (app_id), o talento e o token da sessão,
           igual ao botão "Treinar para Entrevista" do card de candidatura (VacancyCard). -->
      <section v-else-if="tab === 'treino'" class="career-training">
        <div class="career-card">
          <div class="career-card__icon">
            <feather-icon icon="MessageCircleIcon" svgClasses="w-7 h-7" />
          </div>
          <div class="career-card__text">
            <h4>Treine sua entrevista com a Rê</h4>
            <p>
              Escolha uma vaga em que você se candidatou e responda perguntas como numa entrevista de verdade. A Rê
              dá dicas para melhorar suas respostas. É de graça e você treina quantas vezes quiser.
            </p>
          </div>
        </div>

        <p v-if="trainingLoading" class="career-training__hint">Buscando suas candidaturas...</p>

        <div v-else-if="trainingVacancies.length" class="career-training__list">
          <div v-for="vacancy in trainingVacancies" :key="vacancy.id" class="career-training__item">
            <div>
              <h5>{{ vacancy.title }}</h5>
              <span v-if="companyName(vacancy)">{{ companyName(vacancy) }}</span>
            </div>
            <button type="button" class="career-card__button" @click="train(vacancy)">
              Treinar para esta vaga
              <feather-icon icon="ExternalLinkIcon" svgClasses="w-5 h-5" />
            </button>
          </div>
        </div>

        <div v-else class="career-training__empty">
          <p>
            O treino é feito para uma vaga em que você se candidatou, com perguntas daquela vaga. Assim que você se
            candidatar a uma vaga aberta, ela aparece aqui.
          </p>
          <button type="button" class="career-card__button career-card__button--outline" @click="goToVacancies">
            Ver vagas
          </button>
        </div>
      </section>

      <club v-else-if="tab === 'clube'" />
    </div>
  </div>
</template>

<script>
import PublicProfileCard from "@/components/dashboard/profile-detail/PublicProfileCard.vue";
import RecrutestBanner from "@/components/dashboard/RecrutestBanner.vue";
import Club from "./Club.vue";
import { fetchClubStatus } from "@/services/club";

// O clube abre primeiro: é o experimento em teste. A primeira da lista é a sub-aba padrão.
const TABS = [
  { key: "clube", label: "Clube de benefícios" },
  { key: "perfil", label: "Perfil público" },
  { key: "teste", label: "Teste comportamental" },
  { key: "treino", label: "Treino de entrevista" },
];

/**
 * Aba Carreira: junta as ferramentas da Recrutei para quem busca emprego, e é o lugar
 * dos próximos experimentos. A sub-aba vai na URL (?aba=), e a rota antiga /club
 * (volta do pagamento da Stripe) abre direto no clube.
 *
 * O clube some quando o Admin desliga ou não libera para esta empresa (`available` da API).
 * Enquanto a resposta não chega ele aparece, para a volta da Stripe não piscar.
 */
export default {
  name: "Career",
  components: { PublicProfileCard, RecrutestBanner, Club },
  data: () => ({
    clubAvailable: true,
    trainingLoading: false,
    trainingLoaded: false,
    trainingVacancies: [],
  }),
  computed: {
    tabs() {
      return this.clubAvailable ? TABS : TABS.filter((t) => t.key !== "clube");
    },
    // Mesma regra da aba Vagas: a linha da sub-aba ativa leva a cor da empresa.
    color() {
      const company = (this.$store.state.company && this.$store.state.company.company) || {};
      return company.base_hexcolor || "#1551b1";
    },
    tab() {
      const fromQuery = this.$route.query.aba;
      if (this.tabs.some((t) => t.key === fromQuery)) return fromQuery;
      return this.$route.name === "dashboard-club" && this.clubAvailable ? "clube" : this.tabs[0].key;
    },
  },
  watch: {
    tab: {
      immediate: true,
      handler(value) {
        if (value === "treino" && !this.trainingLoaded) this.loadTraining();
      },
    },
  },
  created() {
    this.checkClub();
  },
  methods: {
    async checkClub() {
      try {
        const status = await fetchClubStatus(this.$route.params.company_label);
        this.clubAvailable = !status || status.available !== false;
      } catch (e) {
        // Falhou a consulta: deixa a sub-aba, que mostra o próprio erro.
      }
    },
    // Candidaturas em vaga aberta e sem desistência: as mesmas em que o card mostra o treino.
    async loadTraining() {
      this.trainingLoading = true;
      try {
        const { company_label } = this.$route.params;
        const response = await this.$http.get(`/api/v1/talent/${company_label}/user/talent/applications/all?page=1`);
        const items = (((response.data || {}).data || {}).applications || {}).data || [];
        this.trainingVacancies = items.filter((vacancy) => {
          const application = (vacancy.applications || [])[0];
          return application && vacancy.vacancy_status_id === 1 && !application.application_withdrawal_id;
        });
        this.trainingLoaded = true;
      } catch (e) {
        this.trainingVacancies = [];
      } finally {
        this.trainingLoading = false;
      }
    },
    companyName(vacancy) {
      // Vaga anônima não nomeia a empresa (mesma regra do treino).
      if (vacancy.type === "Anônima") return "";
      return (vacancy.company && vacancy.company.name) || "";
    },
    train(vacancy) {
      const application = vacancy.applications[0];
      const token = localStorage.getItem("userToken");
      const link = `${process.env.VUE_APP_TRAINING_INTERVIEW_URL}/interview/${application.talent_uuid}?app_id=${application.id}&token=${token}`;
      if (window.woopra) {
        window.woopra.track("interview-training-click", {
          app_id: application.id,
          talent_uuid: application.talent_uuid,
          vacancy_id: vacancy.id,
          source: "career",
        });
      }
      window.open(link, "_blank");
    },
    goToVacancies() {
      this.$router.push({ name: "dashboard-vacancy", params: this.$route.params }).catch(() => {});
    },
    select(key) {
      if (key === this.tab) return;
      this.$router.replace({ name: "dashboard-career", params: this.$route.params, query: { aba: key } }).catch(() => {});
    },
  },
};
</script>

<style lang="scss" scoped>
.career {
  font-family: Roboto, sans-serif;
  color: #4f4f4f;
  gap: 24px;
  &__intro {
    h3 {
      font-size: 20px;
      font-weight: 600;
      color: #333;
      margin: 0 0 4px;
    }
    p {
      font-size: 16px;
    }
  }
  // Mesmo visual de .nxp-nav-container / .nxp-nav-button (Vacancy.vue).
  &__nav {
    display: flex;
    gap: 8px;
    border-bottom: 0.5px solid #a8a8a8;
    overflow-x: auto;
  }
  &__nav-button {
    flex-shrink: 0;
    padding: 16px 4px;
    border: none;
    border-bottom: 2px solid #fff;
    background: #fff;
    color: #696969;
    font-family: Roboto, sans-serif;
    font-size: 16px;
    cursor: pointer;
    white-space: nowrap;
    &.is-active {
      color: #333;
    }
  }
  // O Club.vue traz a própria margem de topo; dentro da aba ela já vem do gap.
  &__content ::v-deep .club {
    margin-top: 0 !important;
    padding-bottom: 0 !important;
  }
}
.career-training {
  display: flex;
  flex-direction: column;
  gap: 16px;
  &__hint {
    font-size: 16px;
  }
  &__list {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
  &__item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 16px;
    border: 0.5px solid #a8a8a8;
    border-radius: 16px;
    h5 {
      font-size: 18px;
      font-weight: 500;
      color: #333;
      margin: 0 0 2px;
    }
    span {
      font-size: 14px;
      color: #696969;
    }
    .career-card__button {
      margin-top: 0;
      flex-shrink: 0;
    }
    @media (max-width: 576px) {
      flex-direction: column;
      align-items: stretch;
    }
  }
  &__empty {
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 16px;
    border-radius: 16px;
    background: #f6f6f6;
    p {
      font-size: 16px;
      line-height: 20px;
    }
  }
}
.career-card {
  display: flex;
  gap: 24px;
  padding: 24px;
  border: 0.5px solid #a8a8a8;
  border-radius: 16px;
  @media (max-width: 576px) {
    flex-direction: column;
    gap: 16px;
  }
  &__icon {
    flex-shrink: 0;
    width: 56px;
    height: 56px;
    border-radius: 9999px;
    background: rgba(65, 105, 225, 0.18);
    color: #4169e1;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  &__text {
    display: flex;
    flex-direction: column;
    gap: 8px;
    h4 {
      font-size: 20px;
      font-weight: 600;
      color: #333;
      margin: 0;
    }
    p {
      font-size: 16px;
      line-height: 18.75px;
    }
  }
  &__button {
    border: none;
    cursor: pointer;
    font-family: Roboto, sans-serif;
    align-self: flex-start;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    height: 40px;
    margin-top: 8px;
    padding: 8px 16px;
    border-radius: 8px;
    background: #4169e1;
    color: #fff;
    font-size: 16px;
    &:hover {
      background: #1e3a8a;
      color: #fff;
    }
    @media (max-width: 576px) {
      align-self: stretch;
      justify-content: center;
    }
    &--outline {
      border: 0.5px solid #a8a8a8;
      background: #fff;
      color: #4f4f4f;
      &:hover {
        background: #e7edf0;
        color: #4f4f4f;
      }
    }
  }
}
</style>
